import type { PublicBoard, PublicCoopState, PublicPlayer } from '@bioforge/protocol';
import { Container, Graphics, Rectangle, Text } from 'pixi.js';
import { attachGestures } from '../input/touch.js';
import { ABILITY_NAMES, BOSS_DAMAGE_HINTS, BOSS_NAMES } from '../labels.js';
import { idle } from '../ui/motion.js';
import { artSprite } from '../ui/textures.js';
import { COLOR, textStyle } from '../ui/theme.js';
import { panel, speechBubble } from '../ui/widgets.js';
import { capsulesWord, formatScore } from './battle.js';
import { TEAL_HP, abilityRing, capsuleRow, caps, hpBar } from './battle-panels.js';
import {
  fitCell,
  hudTile,
  isBossId,
  matchPad,
  renderBoardJar,
  type NetControls,
} from './duel-view.js';
import { JarView, jarSize, type JarSkin } from './jar-view.js';

export interface CoopData {
  readonly team: PublicBoard;
  readonly coop: PublicCoopState;
  readonly players: readonly PublicPlayer[];
  readonly playerId: string | null;
  /** Сколько осталось Стратегу на выбор, мс (считает экран от появления вариантов). */
  readonly choiceLeftMs: number;
  readonly skin: JarSkin | null;
  readonly onChoose: (optionId: string) => void;
}

const JAR = { x: 372, y: 108, w: 330, h: 840 - 108 } as const;
/** Телефон: банка по центру между сводкой и управлением. */
const JAR_TALL = { x: 22, y: 192, w: 676, h: 940 - 192 } as const;

/**
 * Кооператив по макету «Кооператив · общий босс»: слева роли и выбор Стратега, по центру общая
 * банка и босс, справа его здоровье, способности и общие очки.
 */
export class CoopView extends Container {
  private jar: JarView | null = null;
  private readonly panels = new Container();
  private readonly stops: (() => void)[] = [];
  private panelsKey = '';

  private stopGestures: (() => void) | null = null;
  private pad: Container | null = null;

  constructor(private readonly controls: NetControls) {
    super();
    this.addChild(this.panels);
  }

  render(data: CoopData): void {
    const { team } = data;
    const pilot = data.coop.pilotId === data.playerId;
    if (!this.jar) {
      const rect = this.controls.portrait ? JAR_TALL : JAR;
      const cell = fitCell(
        team.width,
        team.height,
        rect.w,
        rect.h,
        this.controls.portrait ? 46 : 36,
      );
      const size = jarSize(team.width, team.height, cell);
      this.jar = new JarView(cell);
      this.jar.position.set(rect.x + (rect.w - size.width) / 2, rect.y);
      this.addChildAt(this.jar, 0);
      // Жесты ведут капсулу только у Пилота.
      if (pilot) {
        this.stopGestures = attachGestures(
          this.jar,
          new Rectangle(0, 0, size.width, size.height),
          cell,
          this.controls.onCommand,
        );
      }
    }
    // Кнопки телефона: у Пилота — управление капсулой, у Стратега — его панель выбора.
    if (this.controls.portrait && !this.pad && pilot) {
      this.pad = matchPad(this.controls, true);
      this.pad.position.set(0, 1040);
      this.addChild(this.pad);
    }
    renderBoardJar(this.jar, team, data.skin);
    const key = JSON.stringify([
      team.score,
      team.boss,
      team.abilities,
      team.effects,
      team.next,
      team.virusesRemaining,
      data.coop,
      Math.ceil(data.choiceLeftMs / 1000),
      data.players.map((p) => [p.name, p.connected, p.role]),
    ]);
    if (key === this.panelsKey) return;
    this.panelsKey = key;
    this.stops.splice(0).forEach((stop) => stop());
    this.panels.removeChildren().forEach((child) => child.destroy({ children: true }));
    if (this.controls.portrait) {
      this.portraitPanels(data, pilot);
      return;
    }
    this.left(data);
    this.middle(data);
    this.right(data);
  }

  /** Телефон: сводка (роли и босс), под банкой — способность Пилота или выбор Стратега. */
  private portraitPanels(data: CoopData, pilot: boolean): void {
    const { team, coop } = data;
    const name = (id: string) =>
      id === data.playerId
        ? 'Вы'
        : (data.players.find((p) => p.playerId === id)?.name ?? 'Игрок 2');
    const roles = panel({ width: 330, height: 84, radius: 16 });
    roles.position.set(22, 96);
    const lines: [string, string, number][] = [
      ['Пилот', name(coop.pilotId), COLOR.teal],
      ['Стратег', name(coop.strategistId), COLOR.gold],
    ];
    lines.forEach(([label, who, color], i) => {
      const k = caps(label, color, 12);
      k.position.set(14, 16 + i * 30);
      const t = new Text({ text: who, style: textStyle({ size: 16, weight: '700' }) });
      t.position.set(120, 12 + i * 30);
      if (t.width > 196) t.scale.set(196 / t.width);
      roles.addChild(k, t);
    });
    this.panels.addChild(roles);
    const boss = team.boss;
    if (boss && isBossId(boss.id)) {
      const box = hudTile(364, 334, BOSS_NAMES[boss.id], `${boss.hp}/${boss.maxHp}`, 0x8fe3c9);
      box.children.at(-1)?.scale.set(0.75);
      const bar = hpBar(boss.hp, boss.maxHp, 200, TEAL_HP, 18);
      bar.position.set(118, 46);
      box.addChild(bar);
      this.panels.addChild(box);
    } else {
      this.panels.addChild(
        hudTile(
          364,
          334,
          'Микробы · очки',
          `${team.virusesRemaining} · ${formatScore(team.score)}`,
        ),
      );
    }

    const area = new Container();
    area.position.set(22, 950);
    const options = coop.options;
    const secs = `${Math.ceil(data.choiceLeftMs / 1000)} с`;
    if (pilot) {
      // Строка о выборе Стратега и кольцо Криотерапии в середине верхнего ряда кнопок.
      const t = new Text({
        text: options
          ? `Стратег выбирает следующую капсулу · ${secs}`
          : 'Следующая капсула выбрана',
        style: textStyle({ size: 15, color: options ? COLOR.goldSoft : COLOR.textSoft }),
      });
      t.position.set(0, 0);
      const next = capsuleRow(
        team.next.slice(0, 3),
        26,
        team.effects.frozen > 0,
        data.skin?.capsule === 'candy',
      );
      next.position.set(676 - next.width, -8);
      area.addChild(t, next);
      const cryo = team.abilities[0];
      if (cryo) this.panels.addChild(this.abilityButton(cryo, 0, 360, 1040 + 48));
    } else {
      const title = caps(
        options ? `Выберите следующую капсулу · ${secs}` : 'Пилот ведёт капсулу',
        COLOR.gold,
        13,
      );
      area.addChild(title);
      if (options) {
        const ow = (676 - 12 * (options.length - 1)) / options.length;
        options.forEach((option, i) => {
          const b = new Container();
          b.position.set(i * (ow + 12), 30);
          b.addChild(
            new Graphics()
              .roundRect(0, 5, ow, 120, 18)
              .fill(COLOR.ink)
              .roundRect(0, 0, ow, 120, 18)
              .fill({ color: 0x3c3414, alpha: 1 })
              .stroke({ width: 3, color: COLOR.gold }),
          );
          const pieces = capsuleRow([option.colors], 40, false, data.skin?.capsule === 'candy');
          pieces.position.set((ow - 80) / 2, 24);
          const label = new Text({
            text: `Вариант ${i + 1}`,
            style: textStyle({ size: 14, color: COLOR.goldSoft }),
          });
          label.anchor.set(0.5, 0);
          label.position.set(ow / 2, 78);
          b.addChild(pieces, label);
          b.eventMode = 'static';
          b.cursor = 'pointer';
          b.accessible = true;
          b.accessibleType = 'button';
          b.accessibleTitle = `Вариант ${i + 1}`;
          b.on('pointertap', () => data.onChoose(option.id));
          area.addChild(b);
        });
        const share =
          coop.choiceTimeoutMs > 0 ? Math.max(0, data.choiceLeftMs / coop.choiceTimeoutMs) : 0;
        area.addChild(
          new Graphics()
            .roundRect(0, 168, 676, 8, 4)
            .fill({ color: COLOR.gold, alpha: 0.15 })
            .roundRect(0, 168, 676 * share, 8, 4)
            .fill(COLOR.gold),
        );
      } else {
        const next = capsuleRow(
          team.next.slice(0, 3),
          40,
          team.effects.frozen > 0,
          data.skin?.capsule === 'candy',
        );
        next.position.set(0, 40);
        area.addChild(next);
      }
      const scanner = team.abilities[1];
      if (scanner) this.panels.addChild(this.abilityButton(scanner, 1, 360, 950 + 250));
    }
    this.panels.addChild(area);
  }

  /** Кольцо способности — кнопка для касания. */
  private abilityButton(
    ability: PublicBoard['abilities'][number],
    slot: 0 | 1,
    x: number,
    y: number,
  ): Container {
    const ready = ability.energy >= ability.cost;
    const ring = abilityRing(
      ability.kind,
      ability.energy / ability.cost,
      ready,
      44,
      this.stops,
      slot === 0 ? 0x7fd8ff : COLOR.gold,
    );
    ring.position.set(x, y);
    ring.eventMode = 'static';
    ring.cursor = 'pointer';
    ring.accessible = true;
    ring.accessibleType = 'button';
    const name = ABILITY_NAMES[ability.kind as keyof typeof ABILITY_NAMES] ?? ability.kind;
    ring.accessibleTitle = `${name}${ready ? ', готово' : ''}`;
    ring.on('pointertap', () => this.controls.onAbility(slot));
    return ring;
  }

  private left(data: CoopData): void {
    const { team, coop } = data;
    const col = new Container();
    col.position.set(40, 96);
    const w = 300;
    const roles: ['pilot' | 'strategist', string, number, number][] = [
      ['pilot', 'Пилот', COLOR.teal, 0],
      ['strategist', 'Стратег', COLOR.gold, 1],
    ];
    let y = 0;
    for (const [role, label, color, slot] of roles) {
      const id = role === 'pilot' ? coop.pilotId : coop.strategistId;
      const player = data.players.find((p) => p.playerId === id);
      const mine = id === data.playerId;
      const ability = team.abilities[slot];
      const card = new Container();
      card.y = y;
      const h = 158;
      card.addChild(
        role === 'pilot'
          ? panel({
              width: w,
              height: h,
              radius: 16,
              border: mine ? COLOR.teal : COLOR.border,
              borderWidth: mine ? 3 : 2,
            })
          : panel({
              width: w,
              height: h,
              radius: 16,
              top: 0x3c3414,
              bottom: 0x1e180a,
              border: mine ? COLOR.gold : COLOR.goldBorder,
              borderWidth: mine ? 3 : 2,
            }),
      );
      const badge = caps(label, COLOR.ink, 12);
      const bw = badge.width + 18;
      card.addChild(new Graphics().roundRect(16, 14, bw, 26, 8).fill(color));
      badge.position.set(25, 20);
      const name = new Text({
        text: mine ? 'Вы' : (player?.name ?? 'Игрок 2'),
        style: textStyle({ size: 16, display: true, weight: '800' }),
      });
      name.position.set(16 + bw + 10, 16);
      const doing =
        role === 'pilot' ? 'ведёт капсулу' : coop.options ? 'выбирает…' : 'выбор сделан';
      const state = new Text({
        text: player && !player.connected ? 'нет связи' : doing,
        style: textStyle({ size: 12, color: role === 'pilot' ? COLOR.mint : COLOR.goldSoft }),
      });
      state.anchor.set(1, 0);
      state.position.set(w - 16, 20);
      const desc = new Text({
        text:
          role === 'pilot'
            ? 'Двигает, вращает и ускоряет капсулу'
            : 'Выбирает следующую капсулу из вариантов сервера',
        style: textStyle({
          size: 13,
          color: role === 'pilot' ? COLOR.textSoft : 0xe8d9b0,
          wrap: w - 32,
        }),
      });
      desc.position.set(16, 48);
      // Длинное имя не наезжает на статус справа.
      const room = w - (16 + bw + 10) - state.width - 28;
      if (name.width > room) name.scale.set(room / name.width);
      card.addChild(badge, name, state, desc);
      if (ability) {
        const ready = ability.energy >= ability.cost;
        const ring = abilityRing(
          ability.kind,
          ability.energy / ability.cost,
          ready,
          24,
          this.stops,
          role === 'pilot' ? 0x7fd8ff : COLOR.gold,
        );
        ring.position.set(16 + 24, 122);
        const an = new Text({
          text: ABILITY_NAMES[ability.kind as keyof typeof ABILITY_NAMES] ?? ability.kind,
          style: textStyle({ size: 15, weight: '700' }),
        });
        an.position.set(74, 102);
        const scan = team.effects.scan > 0 && ability.kind === 'scanner';
        const as = new Text({
          text: scan
            ? `Сканер действует · осталось ${team.effects.scan}`
            : ready
              ? 'Готово · C'
              : `Заряд ${Math.round((ability.energy / ability.cost) * 100)}% · C`,
          style: textStyle({ size: 12, color: role === 'pilot' ? 0x7fd8ff : COLOR.goldSoft }),
        });
        as.position.set(74, 124);
        card.addChild(ring, an, as);
      }
      col.addChild(card);
      y += h + 12;
    }

    // Выбор Стратега: варианты сервера и таймер; Стратег выбирает клавишами 1–3 или касанием.
    const choice = new Container();
    choice.y = y;
    const strategist = coop.strategistId === data.playerId;
    const options = coop.options;
    const h = 170;
    choice.addChild(
      new Graphics()
        .roundRect(0, 0, w, h, 16)
        .fill({ color: COLOR.panelBottom, alpha: 0.9 })
        .stroke({ width: 2, color: COLOR.goldBorder }),
    );
    const title = caps(options ? 'Выбор Стратега' : 'Следующая капсула', COLOR.gold);
    title.position.set(16, 14);
    choice.addChild(title);
    if (options) {
      const secs = new Text({
        text: `${Math.ceil(data.choiceLeftMs / 1000)} с`,
        style: textStyle({ size: 14, display: true, weight: '800' }),
      });
      secs.anchor.set(1, 0);
      secs.position.set(w - 16, 12);
      choice.addChild(secs);
      const ow = (w - 32 - 8 * (options.length - 1)) / options.length;
      options.forEach((option, i) => {
        const box = new Container();
        box.position.set(16 + i * (ow + 8), 40);
        box.addChild(
          new Graphics()
            .roundRect(0, 0, ow, 76, 12)
            .fill({ color: COLOR.night, alpha: 0.5 })
            .stroke({ width: 2, color: 0x3a4a6a }),
        );
        const pieces = capsuleRow([option.colors], 24, false, data.skin?.capsule === 'candy');
        pieces.position.set((ow - 48) / 2, 12);
        const label = new Text({
          text: strategist ? `клавиша ${i + 1}` : 'вариант',
          style: textStyle({ size: 11, color: COLOR.textMuted }),
        });
        label.anchor.set(0.5, 0);
        label.position.set(ow / 2, 46);
        box.addChild(pieces, label);
        if (strategist) {
          box.eventMode = 'static';
          box.cursor = 'pointer';
          box.accessible = true;
          box.accessibleType = 'button';
          box.accessibleTitle = `Вариант ${i + 1}`;
          box.on('pointertap', () => data.onChoose(option.id));
        }
        choice.addChild(box);
      });
      const share =
        coop.choiceTimeoutMs > 0 ? Math.max(0, data.choiceLeftMs / coop.choiceTimeoutMs) : 0;
      choice.addChild(
        new Graphics()
          .roundRect(16, 126, w - 32, 6, 3)
          .fill({ color: COLOR.gold, alpha: 0.15 })
          .roundRect(16, 126, (w - 32) * share, 6, 3)
          .fill(COLOR.gold),
      );
      const note = new Text({
        text: 'Если время выйдет, сервер выберет сам',
        style: textStyle({ size: 12, color: COLOR.textMuted }),
      });
      note.position.set(16, 142);
      choice.addChild(note);
    } else {
      const next = team.next.slice(0, 3);
      const pieces = capsuleRow(next, 30, team.effects.frozen > 0, data.skin?.capsule === 'candy');
      pieces.position.set(16, 50);
      const note = new Text({
        text:
          next.length > 1
            ? 'Сканер: видно несколько капсул вперёд'
            : 'Стратег выбрал — Пилот ведёт её следующей',
        style: textStyle({ size: 12, color: COLOR.textMuted, wrap: w - 32 }),
      });
      note.position.set(16, 100);
      choice.addChild(pieces, note);
    }
    col.addChild(choice);
    this.panels.addChild(col);
  }

  /** Босс за стеклом — крупно рядом с банкой; босс в сосуде — только реплика. */
  private middle(data: CoopData): void {
    const boss = data.team.boss;
    if (!boss || !isBossId(boss.id)) return;
    const bubble = speechBubble(BOSS_NAMES[boss.id], 'Вдвоём не справитесь!', 240, 'bottom');
    bubble.position.set(760, 90);
    this.panels.addChild(bubble);
    this.stops.push(idle(bubble, 'talk'));
    if (boss.placement === 'vessel') return;
    const big = new Container();
    big.position.set(866, 400);
    const sprite = artSprite(`boss-${boss.id}`, 340);
    big.addChild(sprite);
    this.stops.push(idle(sprite, 'boss'));
    const next = boss.abilities
      .filter((a) => !a.done && a.countdown !== null)
      .sort((a, b) => (a.countdown ?? 0) - (b.countdown ?? 0))[0];
    if (next && next.countdown !== null) {
      const chip = new Container();
      const t = caps(
        `${next.name} через ${next.countdown} ${capsulesWord(next.countdown)}`,
        COLOR.white,
        11,
      );
      const cw = t.width + 24;
      chip.addChild(
        new Graphics()
          .roundRect(0, 3, cw, 30, 15)
          .fill(COLOR.ink)
          .roundRect(0, 0, cw, 30, 15)
          .fill(0x9b6bff),
      );
      t.position.set(12, 9);
      chip.addChild(t);
      chip.position.set(60 - cw / 2, 130);
      big.addChild(chip);
    }
    this.panels.addChild(big);
  }

  private right(data: CoopData): void {
    const { team } = data;
    const col = new Container();
    col.position.set(1080, 96);
    const w = 320;
    let y = 0;
    const boss = team.boss;
    if (boss && isBossId(boss.id)) {
      const hint = new Text({
        text: BOSS_DAMAGE_HINTS[boss.placement],
        style: textStyle({ size: 13, color: 0xc9f5e5, wrap: w - 32, lineHeight: 18 }),
      });
      const h = 16 + 16 + 34 + 18 + 4 + 22 + 10 + hint.height + 16;
      const card = new Container();
      card.addChild(
        panel({
          width: w,
          height: h,
          radius: 18,
          top: 0x14463c,
          bottom: 0x0a1e20,
          border: 0x2f8f7a,
        }),
      );
      const k = caps(
        boss.placement === 'vessel' ? 'Общий босс · в сосуде' : 'Общий босс · вне сосуда',
        0x8fe3c9,
      );
      k.position.set(16, 16);
      const name = new Text({
        text: BOSS_NAMES[boss.id],
        style: textStyle({ size: 26, display: true, weight: '900', shadow: 3 }),
      });
      name.position.set(16, 34);
      const hl = new Text({ text: 'Здоровье', style: textStyle({ size: 13, color: 0xc9f5e5 }) });
      hl.position.set(16, 72);
      const hv = new Text({
        text: `${boss.hp} / ${boss.maxHp}`,
        style: textStyle({ size: 14, display: true, weight: '800' }),
      });
      hv.anchor.set(1, 0);
      hv.position.set(w - 16, 72);
      const bar = hpBar(boss.hp, boss.maxHp, w - 32, TEAL_HP, 22);
      bar.position.set(16, 94);
      hint.position.set(16, 126);
      card.addChild(k, name, hl, hv, bar, hint);
      col.addChild(card);
      y += h + 12;

      const list = new Container();
      list.y = y;
      const rows = boss.abilities.map((a) => {
        const counting = !a.done && a.countdown !== null;
        const when = a.done
          ? 'сработала'
          : a.countdown !== null
            ? `через ${a.countdown} ${capsulesWord(a.countdown)}`
            : a.belowHp !== null
              ? `при здоровье ${a.belowHp} и ниже`
              : 'ждёт';
        return { a, counting, when };
      });
      const lh = 16 + 14 + 14 + rows.length * 56 + 4;
      list.addChild(
        new Graphics()
          .roundRect(0, 0, w, lh, 16)
          .fill({ color: 0x0a1e20, alpha: 0.9 })
          .stroke({ width: 2, color: 0x1e5a50 }),
      );
      const lk = caps('Способности босса', 0x8fe3c9);
      lk.position.set(16, 16);
      list.addChild(lk);
      rows.forEach(({ a, counting, when }, i) => {
        const row = new Container();
        row.position.set(16, 44 + i * 56);
        row.alpha = counting ? 1 : 0.6;
        row.addChild(
          new Graphics()
            .roundRect(0, 0, 36, 36, 10)
            .fill(counting ? 0x9b6bff : 0x1e3a3a)
            .stroke({ width: 2, color: COLOR.ink }),
        );
        const icon = artSprite(`icon-boss-${a.kind}`, 20);
        icon.position.set(18, 18);
        const n = new Text({ text: a.name, style: textStyle({ size: 15, weight: '700' }) });
        n.position.set(48, 0);
        const t = new Text({ text: when, style: textStyle({ size: 13, color: 0xd7c2ff }) });
        t.position.set(48, 22);
        row.addChild(icon, n, t);
        list.addChild(row);
      });
      col.addChild(list);
      y += lh + 12;
    } else {
      const card = new Container();
      card.addChild(panel({ width: w, height: 110, radius: 18 }));
      const k = caps('Общая банка');
      k.position.set(16, 16);
      const t = new Text({
        text: `Осталось микробов: ${team.virusesRemaining}`,
        style: textStyle({ size: 18, display: true, weight: '800' }),
      });
      t.position.set(16, 40);
      const d = new Text({
        text: 'Очистите её вместе, пока не кончились ходы',
        style: textStyle({ size: 13, color: COLOR.textSoft }),
      });
      d.position.set(16, 74);
      card.addChild(k, t, d);
      col.addChild(card);
      y += 110 + 12;
    }
    const score = new Container();
    score.y = y;
    score.addChild(
      new Graphics()
        .roundRect(0, 0, w, 56, 16)
        .fill({ color: COLOR.panelBottom, alpha: 0.85 })
        .stroke({ width: 2, color: COLOR.borderDim }),
    );
    const sl = new Text({
      text: 'Общие очки',
      style: textStyle({ size: 13, color: COLOR.textMuted }),
    });
    sl.anchor.set(0, 0.5);
    sl.position.set(16, 28);
    const sv = new Text({
      text: formatScore(team.score),
      style: textStyle({ size: 22, display: true, weight: '900' }),
    });
    sv.anchor.set(1, 0.5);
    sv.position.set(w - 16, 28);
    score.addChild(sl, sv);
    col.addChild(score);
    this.panels.addChild(col);
  }

  override destroy(): void {
    this.stopGestures?.();
    this.stops.splice(0).forEach((stop) => stop());
    super.destroy({ children: true });
  }
}
